'use client';

import { useTranslations } from 'next-intl';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import type { CreditV1Coupon } from '../types';

interface CouponSelectProps {
  availableCoupons: CreditV1Coupon[];
  lockedCouponIds?: Set<string>;
  selectedCouponId: string | null;
  onChange: (id: string | null) => void;
}

const NO_COUPON = '__none__';

export const CouponSelect = ({ availableCoupons, lockedCouponIds, selectedCouponId, onChange }: CouponSelectProps) => {
  const t = useTranslations('payments.stripeConfirm');

  return (
    <Select value={selectedCouponId ?? NO_COUPON} onValueChange={(val) => onChange(val === NO_COUPON ? null : val)}>
      <SelectTrigger className="h-11 border-system-white08 bg-system-white08 text-sm text-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]">
        <SelectItem value={NO_COUPON} className="flex h-11 items-center [&>span]:min-w-0 [&>span]:flex-1">
          <span className="block w-full truncate">{t('noCoupon')}</span>
        </SelectItem>
        {availableCoupons
          .filter((c) => c.id)
          .map((c) => {
            const isLocked = Boolean(c.id && lockedCouponIds?.has(c.id));
            const name = c.name ?? '';
            return (
              <SelectItem
                key={c.id}
                value={c.id ?? ''}
                disabled={isLocked}
                className="flex h-11 items-center [&>span]:min-w-0 [&>span]:flex-1"
              >
                <CustomTooltip
                  container={document.body}
                  content={name}
                  side="top"
                  align="center"
                  showOnlyOnOverflow
                  triggerClassName="block w-full truncate"
                >
                  {name}
                </CustomTooltip>
              </SelectItem>
            );
          })}
      </SelectContent>
    </Select>
  );
};
